import type { Program } from '../types/programs';
import type { ProgramTemplate, ProgramTemplateWeek } from '../types/templates';
import { atLocalMidnight } from './weekdays';
import { toSun0Week } from './templatePattern';

const toISO = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Extract any day-of-week hint the coach/editor may provide.
function _extractCoachDow(p: any): unknown {
  return (
    p.sun0 ??
    p.weekdaySun0 ??
    p.weekday ??
    p.dayOfWeek ??
    p.day_of_week ??
    p.weekdayMon0 ??
    p.weekdayMon1 ??
    p.dayName ??
    p.day ??
    null
  );
}

/**
 * Expand a ProgramTemplate into a concrete Program starting at startDate (local).
 * The weekly pattern repeats for totalWeeks.
 * Normalizes coach weekday inputs to Sun=0..Sat=6.
 */
export function expandProgramTemplate(
  tmpl: ProgramTemplate,
  programId: string,
  startDate: Date,
  programName?: string,
): Program {
  const start = atLocalMidnight(startDate);
  const days = [] as Program['days'];

  function getWeekPattern(weekIndex: number) {
    const hasPerWeek = Array.isArray(tmpl.weeks) && tmpl.weeks.length > 0;
    if (hasPerWeek) {
      const perWeek: ProgramTemplateWeek | undefined = tmpl.weeks!.find((w) => w.week === weekIndex + 1);
      // Missing or empty per-week pattern => treat as Rest week (no inheritance)
      return toSun0Week(perWeek?.pattern);
    }
    if (Array.isArray(tmpl.pattern)) return toSun0Week(tmpl.pattern);
    return toSun0Week([]);
  }

  for (let w = 0; w < tmpl.totalWeeks; w++) {
    // Determine the calendar Sunday of this program week (so weekSpec[0] maps to Sun, ... [6] → Sat)
    const anchor = new Date(start);
    anchor.setDate(start.getDate() + w * 7);
    const weekStartSun = new Date(anchor);
    weekStartSun.setDate(anchor.getDate() - anchor.getDay()); // shift to Sunday
    const weekSpec = getWeekPattern(w); // Sun→Sat specification
    for (let d = 0; d < 7; d++) {
      const cur = new Date(weekStartSun);
      cur.setDate(weekStartSun.getDate() + d);
      const spec = weekSpec[d];
      days.push({
        date: toISO(cur),
        type: spec.type,
        title: spec.title,
        durationMin: spec.durationMin,
        meals: spec.meals,
        workoutId: spec.workoutId ? String(spec.workoutId) : undefined,
        // carry snapshot when inline exercises are present
        exerciseSnapshot: Array.isArray(spec.exercises) && spec.exercises.length ? spec.exercises : undefined,
      } as any);
    }
  }

  return {
    id: programId,
    name: programName ?? tmpl.name,
    start: toISO(start),
    totalWeeks: tmpl.totalWeeks,
    days,
  };
}
